import { type ReactElement } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Typography,
  Box,
  Link,
  Divider,
  FormLabel,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { schemaValidate } from '../../validation/modal-confirm-email-mui';

interface FormData {
  code: string;
}

interface ModalConfirmEmailProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (code: string) => void;
  onResend: () => void;
  loading?: boolean;
}

const ModalConfirmEmail = ({
  open,
  onClose,
  onConfirm,
  onResend,
  loading = false,
}: ModalConfirmEmailProps): ReactElement => {
  const {
    control,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<FormData>({
    resolver: yupResolver(schemaValidate),
    defaultValues: {
      code: '',
    },
  });

  const onSubmit = (data: FormData): void => {
    onConfirm(data.code);
  };

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const handleResend = (): void => {
    onResend();
  };

  return (
    <Dialog
      open={open}
      onClose={(_event, reason): void => {
        if (reason && reason === 'backdropClick') return;
        handleClose();
      }}
      maxWidth='xs'
      fullWidth
      disableEscapeKeyDown
      PaperProps={{
        sx: {
          borderRadius: 2,
          minHeight: 300,
        },
      }}
    >
      <DialogTitle
        sx={{
          fontSize: '16px',
          fontWeight: 600,
          color: '#374151',
          py: 2,
        }}
      >
        確認コード入力
      </DialogTitle>
      <Divider />

      <DialogContent sx={{ px: 3, pb: 2 }}>
        <Box sx={{ mb: 3 }}>
          <Typography
            variant='body2'
            sx={{
              color: '#6b7280',
              lineHeight: 1.6,
            }}
          >
            登録したメールに確認コードが送信されました、ご確認してください。
            <br />
            <Box component='span'>
              確認コードが届かない場合は、
              <Link
                onClick={handleResend}
                sx={{
                  color: '#0b5ed7',
                  cursor: 'pointer',
                  textDecoration: 'none',
                  '&:hover': {
                    textDecoration: 'underline',
                    opacity: 0.8,
                  },
                }}
              >
                「こちら」
              </Link>
              をクリックしてください。
            </Box>
          </Typography>
        </Box>

        <Box component='form' onSubmit={handleSubmit(onSubmit)}>
          <Controller
            name='code'
            control={control}
            render={({ field }) => (
              <>
                <FormLabel
                  sx={{
                    fontSize: '13px',
                    fontWeight: 'bold',
                    color: '#111827',
                  }}
                >
                  確認コード入力
                  <span style={{ color: 'red', marginLeft: '4px' }}>＊</span>
                </FormLabel>
                <TextField
                  {...field}
                  fullWidth
                  label={''}
                  placeholder='確認コードを入力してください'
                  error={!!errors.code}
                  helperText={errors.code?.message}
                  inputProps={{ maxLength: 6 }}
                  size='small'
                  sx={{
                    '& .MuiFormHelperText-root': {
                      margin: 0,
                      fontSize: '12px',
                    },
                    '& .MuiInputBase-input::placeholder': {
                      fontSize: '12px',
                      opacity: 0.7,
                    },
                  }}
                />
              </>
            )}
          />
        </Box>
      </DialogContent>
      <Divider />
      <DialogActions
        sx={{
          px: 3,
          py: 2,
          gap: 2,
          justifyContent: 'end',
        }}
      >
        <Button
          onClick={handleClose}
          variant='outlined'
          disabled={loading}
          sx={{
            minWidth: 100,
            borderColor: '#0b5ed7',
            color: '#0b5ed7',
            '&:hover': {
              borderColor: '#0a4bb5',
              backgroundColor: 'rgba(11, 94, 215, 0.04)',
            },
          }}
        >
          キャンセル
        </Button>
        <Button
          onClick={handleSubmit(onSubmit)}
          variant='contained'
          disabled={loading}
          sx={{
            minWidth: 100,
            backgroundColor: '#0b5ed7',
            '&:hover': {
              backgroundColor: '#0a4bb5',
            },
            '&:disabled': {
              backgroundColor: '#9ca3af',
            },
          }}
        >
          {loading ? '確認中...' : '確認'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ModalConfirmEmail;
